import React from 'react';
import { EnvelopeIcon, PhoneIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { Lead } from '../../types/lead.types';
import { SchoolSegment } from '../../types/project.types';
import { getStatusColor } from '../../utils/leadColors';
import { getBusinessDaysRemaining } from '../../utils/dateUtils';
import { LeadSegmentPill } from './LeadSegmentPill';

interface LeadCardProps {
  lead: Lead;
  onClick: () => void;
  validationDeadlineDays?: number;
  segments?: SchoolSegment[];
}

const LeadCard: React.FC<LeadCardProps> = ({ lead, onClick, validationDeadlineDays, segments }) => {

  const getParentName = () => {
    if (lead.parentName) return lead.parentName;
    if (lead.parentInfo) {
      const { firstName, lastName } = lead.parentInfo;
      if (firstName || lastName) {
        return `${firstName || ''} ${lastName || ''}`.trim();
      }
    }
    return 'No Name';
  };

  const getAddress = () => {
    if (lead.city && lead.state) return `${lead.city}, ${lead.state}`;
    if (lead.address) return lead.address;
    return 'No address';
  };

  const getStatusLabel = (status: string) => {
    return status === 'dnc' ? 'DNC' : status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer p-6 border border-gray-200"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="min-w-0">
          <h3 className="text-lg font-medium text-gray-900">
            {getParentName()}
          </h3>
          <LeadSegmentPill lead={lead} segments={segments} className="mt-1" />
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(lead.status || 'uncontacted')}`}>
            {getStatusLabel(lead.status || 'uncontacted')}
          </span>
          {validationDeadlineDays && lead.determination === 'pending' && lead.status === 'uncontacted' && lead.createdAt && (() => {
            const { daysRemaining, isOverdue } = getBusinessDaysRemaining(lead.createdAt.toDate(), validationDeadlineDays);
            const color = isOverdue ? 'bg-red-100 text-red-800' : daysRemaining <= 2 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800';
            const label = isOverdue ? 'Overdue' : `${daysRemaining}d left`;
            return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{label}</span>;
          })()}
        </div>
      </div>
      
      <div className="space-y-2 text-sm text-gray-600">
        <div className="flex items-center">
          <EnvelopeIcon className="h-4 w-4 mr-2" />
          {lead.email || lead.parentInfo?.email || 'No email'}
        </div>
        <div className="flex items-center">
          <PhoneIcon className="h-4 w-4 mr-2" />
          {lead.phone || lead.parentInfo?.phone || 'No phone'}
        </div>
        <div className="flex items-center">
          <MapPinIcon className="h-4 w-4 mr-2" />
          {getAddress()}
        </div>
      </div>

      {lead.children?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            {lead.children.length} child{lead.children.length !== 1 ? 'ren' : ''}
          </p>
        </div>
      )}
    </div>
  );
};

export default LeadCard;
